import JSBI from 'jsbi'
import { AggregatorQuote, QuoteProvider, QuoteRequest, RouteHop } from '../types'
import { buildQuery, endpointFor, getJson } from '../http'
import { feeAsPercent, revenueConfig } from '../revenue'

/**
 * OpenOcean.
 *
 * NOT ENABLED BY DEFAULT. Originally chosen because its quote API needs no key, but
 * that endpoint returns HTTP 403 behind Cloudflare bot protection for every
 * non-interactive client - tested from a browser on our own origin, from an honestly
 * identified server, and from an anonymous one. The only way past is to disguise the
 * client as a browser, which is bot-detection evasion and would break as soon as they
 * tightened it.
 *
 * Kept intact and tested so it can be switched back on via REACT_APP_QUOTE_PROVIDERS
 * if OpenOcean grants proper API access. Revenue rides on `referrer` + `referrerFee`,
 * where the fee is a *percentage* (0.25 = 0.25%).
 *
 * NOTE ON FIELD NAMES: never confirmed against a live response, for the reason above.
 * Parsing is isolated in `normalise`; correcting it touches nothing else.
 */

const DIRECT_BASE = 'https://open-api.openocean.finance/v3'

const CHAIN_SLUGS: { [chainId: number]: string } = {
  56: 'bsc',
}

interface OpenOceanDex {
  dexName?: string
  name?: string
  /** Share of the trade through this venue; OpenOcean reports a percentage. */
  percentage?: number
}

interface OpenOceanQuoteData {
  inAmount?: string
  outAmount?: string
  minOutAmount?: string
  estimatedGas?: string | number
  to?: string
  data?: string
  value?: string
  priceImpact?: string | number
  dexes?: OpenOceanDex[]
  path?: { routes?: { subRoutes?: { dexes?: OpenOceanDex[] }[] }[] }
}

interface OpenOceanResponse {
  code?: number
  data?: OpenOceanQuoteData
  error?: string
  message?: string
}

/** Smallest-unit string -> human decimal string, without floating point. */
export function toHumanAmount(amount: string, decimals: number): string {
  const padded = amount.padStart(decimals + 1, '0')
  const whole = padded.slice(0, padded.length - decimals)
  const fraction = padded.slice(padded.length - decimals).replace(/0+$/, '')
  return fraction ? `${whole}.${fraction}` : whole
}

function applySlippage(outAmount: string | undefined, slippageBps: number): string {
  if (!outAmount) return '0'
  try {
    const out = JSBI.BigInt(outAmount)
    const keep = JSBI.BigInt(10_000 - slippageBps)
    return JSBI.divide(JSBI.multiply(out, keep), JSBI.BigInt(10_000)).toString()
  } catch {
    return '0'
  }
}

function extractRoute(data: OpenOceanQuoteData): RouteHop[] {
  const flat: OpenOceanDex[] = []

  if (Array.isArray(data.dexes)) flat.push(...data.dexes)
  data.path?.routes?.forEach((route) =>
    route.subRoutes?.forEach((sub) => sub.dexes && flat.push(...sub.dexes))
  )

  return flat
    .map((dex) => ({
      source: dex.dexName ?? dex.name ?? 'Unknown',
      portionBps: Math.round((dex.percentage ?? 0) * 100),
    }))
    .filter((hop) => hop.portionBps > 0)
}

function toPriceImpactBps(raw: string | number | undefined): number | undefined {
  if (raw === undefined) return undefined
  // OpenOcean returns either "-1.23" or "-1.23%" depending on endpoint.
  const numeric = typeof raw === 'number' ? raw : Number.parseFloat(String(raw).replace('%', ''))
  return Number.isFinite(numeric) ? Math.round(numeric * 100) : undefined
}

export function normalise(response: OpenOceanResponse, request: QuoteRequest): AggregatorQuote {
  if (response.code !== undefined && response.code !== 200 && response.code !== 0) {
    throw new Error(response.error ?? response.message ?? `OpenOcean returned code ${response.code}`)
  }
  const { data } = response
  if (!data?.outAmount) throw new Error('No route found')

  const minBuyAmount = data.minOutAmount ?? applySlippage(data.outAmount, request.slippageBps)

  return {
    providerId: 'openocean',
    providerLabel: 'OpenOcean',
    sellAmount: data.inAmount ?? request.sellAmount,
    buyAmount: data.outAmount,
    minBuyAmount,
    estimatedGas: data.estimatedGas !== undefined ? String(data.estimatedGas) : undefined,
    // OpenOcean settles through the contract it names in `to`, so that is also the
    // address the sell token must be approved to.
    allowanceTarget: data.to,
    route: extractRoute(data),
    priceImpactBps: toPriceImpactBps(data.priceImpact),
    fee: revenueConfig.enabled
      ? { bps: revenueConfig.feeBps, recipient: revenueConfig.feeRecipient }
      : undefined,
    transaction:
      data.to && data.data ? { to: data.to, data: data.data, value: data.value ?? '0' } : undefined,
    raw: response,
  }
}

export const openOceanProvider: QuoteProvider = {
  id: 'openocean',
  label: 'OpenOcean',

  supportsChain: (chainId) => Boolean(CHAIN_SLUGS[chainId]),

  // No API key required, so this source is always available.
  isConfigured: () => true,

  async fetchQuote(request, signal) {
    const slug = CHAIN_SLUGS[request.chainId]
    if (!slug) throw new Error(`OpenOcean does not support chain ${request.chainId}`)

    // `swap_quote` returns calldata as well as pricing when a taker is known;
    // `quote` is the cheaper indicative-price call used before a wallet connects.
    const path = request.taker ? `/${slug}/swap_quote` : `/${slug}/quote`

    const query = buildQuery({
      inTokenAddress: request.sellToken.address,
      outTokenAddress: request.buyToken.address,
      // OpenOcean v3 takes the *human* amount, not the smallest unit.
      amount: toHumanAmount(request.sellAmount, request.sellToken.decimals),
      slippage: request.slippageBps / 100,
      gasPrice: 1,
      account: request.taker,
      referrer: revenueConfig.enabled ? revenueConfig.feeRecipient : undefined,
      referrerFee: revenueConfig.enabled ? feeAsPercent() : undefined,
    })

    const url = `${endpointFor('openocean', DIRECT_BASE, path)}?${query}`
    return normalise(await getJson<OpenOceanResponse>(url, signal), request)
  },
}
